<template>
  <form @submit.prevent>
    <h4 class="mb-1">{{ node && node.data && node.data.name | translate }}</h4>
    <p class="text-muted small">{{ $t("inventory.node-form.hint") }}</p>

    <h6 class="mt-3">{{ $t("disposal-method.periodic") }} <span class="text-muted">({{ $t("common.month") }})</span></h6>
    <input v-model="form.disposalPeriod" class="form-control" type="number" min="0" max="360" />

    <node-fields :form="form" @datasources="(v) => (form.datasources = v || [])" @category="(v) => (form.category = v && v._id ? v : null)" />

    <h6 class="mt-4">{{ $t("inventory.node-form.administrative-measures") }}</h6>
    <filter-select id="adminMeasureFilter" ref="adminMeasureFilter" :placeholder="$t('measure.text')" :multiple="true" @filter="(v, cb) => onMeasureSearch('ADMINISTRATIVE', v, cb)" :selected="form.administrativeMeasures" @changed="(v) => (form.administrativeMeasures = v || [])" />
    <h6 class="mt-4">{{ $t("inventory.node-form.technical-measures") }}</h6>
    <filter-select id="techMeasureFilter" ref="techMeasureFilter" :placeholder="$t('measure.text')" :multiple="true" @filter="(v, cb) => onMeasureSearch('TECHNICAL', v, cb)" :selected="form.technicalMeasures" @changed="(v) => (form.technicalMeasures = v || [])" />

    <h6 class="mt-4">{{ $t("common.note") }}</h6>
    <textarea v-model="form.note" class="form-control" rows="3"></textarea>

    <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-3">
      {{ $t("common.save") }}
    </sweet-button>
  </form>
</template>

<script>
import { categoryChanged, showCategoryWarnings } from "@/modules/process-data/category-warnings";
import NodeFields from "@modules/process-data/component/process-data-node-fields";
import { resolveNodeCategory } from "@/modules/process-data/unified-picker"; // TKP-184 md.76
import { measureSearchQuery } from "@/modules/measure/measure";

/**
 * TKP-27 / 6.2 (kriter 2) — envanter düzenleme TEK formu: süreç×veri düğümünün tüm özellikleri (saklama süresi, kaynaklar
 * K1, kategori override K4, tedbirler 6.5, not) bir yerden. İmha yöntemi / erişim kısıtı satır bazlı uçlarla panelden.
 */
export default {
  components: { NodeFields },
  props: ["id", "node", "process"],
  data() {
    return { form: this.formOf(this.node) };
  },
  computed: {
    isUpdating() {
      return this.$store.getters["organizationProcessData/isUpdating"];
    },
  },
  watch: {
    node: {
      deep: true,
      handler(v) {
        this.form = this.formOf(v);
      },
    },
  },
  methods: {
    formOf(node) {
      return {
        disposalPeriod: node?.feature?.disposalPeriod ?? 0,
        datasources: node?.datasources ?? [],
        category: node?.categoryOverridden ? node.category : null,
        administrativeMeasures: node?.administrativeMeasures ?? [],
        technicalMeasures: node?.technicalMeasures ?? [],
        note: node?.note ? node.note[this.currentLanguage] ?? "" : "",
      };
    },
    /** TKP-162: tedbir kütüphanesi (kategori + mevcut ürün) — eskiden politika kütüphanesi. */
    async onMeasureSearch(category, value, callback) {
      const response = await this.$store.dispatch("measure/paginateForSearch", { options: { limit: 50 }, query: measureSearchQuery(category, this.currentProduct, value) });
      callback(response.data?.docs ?? []);
    },
    async save() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) return;
      const ref = (i) => (i && i._id ? { _id: i._id } : undefined);
      const changed = categoryChanged(this.node.categoryOverridden ? this.node.category : null, this.form.category); // TKP-128: kayıttan ÖNCE
      try {
        // TKP-184 md.76: kütüphaneden seçilen kategori önce organizasyon kategorisine çevrilir
        const category = await resolveNodeCategory(this.$store.dispatch, this.form.category, this.process || { _id: this.node.processId });
        const response = await this.$store.dispatch("organizationProcessData/update", {
          _id: this.node._id,
          datasources: (this.form.datasources ?? []).map(ref).filter(Boolean),
          category: category ? ref(category) : null,
          administrativeMeasures: (this.form.administrativeMeasures ?? []).map(ref).filter(Boolean),
          technicalMeasures: (this.form.technicalMeasures ?? []).map(ref).filter(Boolean),
        });
        if (Number(this.form.disposalPeriod) !== (this.node.feature?.disposalPeriod ?? 0)) {
          await this.$store.dispatch("organizationProcessData/updateDisposalPeriod", { _id: this.node._id, disposalPeriod: this.form.disposalPeriod });
        }
        const currentNote = this.node.note ? this.node.note[this.currentLanguage] ?? "" : "";
        if ((this.form.note ?? "") !== currentNote) {
          await this.$store.dispatch("organizationProcessData/updateNote", { _id: this.node._id, note: this.form.note });
        }
        this.$toast.success(this.$t("common.success"));
        showCategoryWarnings(this, response, { changed }); // TKP-128
        this.$emit("onSaved");
        this.$root.$emit("bv::toggle::collapse", this.id);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
